import React, { useRef, useState, useEffect } from "react";
import {
    Card,
    CardBody,
    Container,
    Row,
    Col,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Alert
} from "reactstrap";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate } from "react-router-dom";
import Api from "../../components/helpers/Api";
import { SeoHelper } from "../../components/helpers/SeoHelper";
import { sessionEnded } from "../user/SessionEnded";

const DEFAULT_BASE_URL = "https://theworshipbible.church";

/**
 * Final GA-safe normalisation (save-time / blur-time)
 * - allows a–z, 0–9, _ and -
 * - converts other runs to _
 * - collapses separators
 */
const normaliseCampaign = (value) =>
    (value || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9_-]+/g, "_")
        .replace(/[_-]{2,}/g, "_")
        .replace(/^[_-]+|[_-]+$/g, "");

/**
 * Gentle typing-time sanitiser
 * - allows spaces while typing
 * - allows _ and -
 * - strips other symbols that will never be allowed anyway
 * This prevents "fighting the cursor" while still keeping it clean-ish.
 */
const sanitiseWhileTyping = (value) =>
    (value || "")
        .toLowerCase()
        .replace(/[^a-z0-9 _-]/g, "");

export default function QrCampaignGenerator() {
    const navigate = useNavigate();
    const qrRef = useRef(null);

    const [form, setForm] = useState({
        baseUrl: DEFAULT_BASE_URL,
        utmSource: "admin",
        utmMedium: "qr",
        utmCampaign: "",
        utmContent: "",
        utmTerm: "",
        intendedUse: "",
        notes: ""
    });

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const customer = sessionStorage.getItem("customer");
        if (!customer || !JSON.parse(customer).user?.isAdmin) {
            navigate("/");
            return;
        }
    }, [navigate]);

    const buildUrl = () => {
        const params = new URLSearchParams();

        Object.entries(form).forEach(([key, value]) => {
            if (key !== "baseUrl" && value) {
                params.append(
                    key.replace(/[A-Z]/g, (m) => "_" + m.toLowerCase()),
                    value
                );
            }
        });

        return `${form.baseUrl}?${params.toString()}`;
    };

    const saveCampaign = async () => {
        const payload = {
            baseUrl: form.baseUrl,
            utmCampaign: normaliseCampaign(form.utmCampaign),
            utmSource: form.utmSource,
            utmMedium: form.utmMedium,
            utmContent: form.utmContent,
            utmTerm: form.utmTerm,
            intendedUse: form.intendedUse,
            notes: form.notes
        };

        await Api.post("/api/admin/qr-campaigns", payload);
    };

    const downloadQr = async () => {
        if (saving) return;

        setSaving(true);
        setError(null);
        setSuccess(false);

        try {
            await saveCampaign();

            const qrCanvas = qrRef.current?.querySelector("canvas");
            if (!qrCanvas) throw new Error("QR canvas not available");

            const size = qrCanvas.width;

            // Create export canvas
            const exportCanvas = document.createElement("canvas");
            exportCanvas.width = size;
            exportCanvas.height = size;

            const ctx = exportCanvas.getContext("2d");
            ctx.drawImage(qrCanvas, 0, 0);

            // Load logo
            const logo = new Image();
            // Helps avoid occasional timing/caching weirdness in some setups
            logo.crossOrigin = "anonymous";
            logo.src = "/qr/worship-bible-qr-logo.png";

            logo.onload = () => {
                const logoSize = size * 0.25; // safe for level H
                const x = (size - logoSize) / 2;
                const y = (size - logoSize) / 2;

                // White safety background
                ctx.fillStyle = "#ffffff";
                ctx.fillRect(x - 6, y - 6, logoSize + 12, logoSize + 12);

                // Draw logo
                ctx.drawImage(logo, x, y, logoSize, logoSize);

                const link = document.createElement("a");
                link.download = `${normaliseCampaign(form.utmCampaign) || "qr-campaign"}.png`;
                link.href = exportCanvas.toDataURL("image/png");
                link.click();

                setSuccess(true);
                setSaving(false);
            };

            logo.onerror = () => {
                // Fallback: download QR without logo if logo fails to load
                const link = document.createElement("a");
                link.download = `${normaliseCampaign(form.utmCampaign) || "qr-campaign"}.png`;
                link.href = qrCanvas.toDataURL("image/png");
                link.click();
                setSuccess(true);
                setSaving(false);
            };
        } catch (err) {
            console.error(err);
            setError(
                err?.response?.data?.message ||
                "Failed to save QR campaign. Please try again."
            );
            setSaving(false);
        }
    };

    if (!sessionStorage.getItem("customer")) {
        return sessionEnded();
    }

    const normalisedPreview = normaliseCampaign(form.utmCampaign);

    return (
        <>
            <SeoHelper noindex />

            <div className="position-relative">
                {/* HERO */}
                <section className="section section-lg section-shaped pb-250">
                    <div className="shape shape-style-1 shape-default">
                        <span /><span /><span /><span /><span /><span /><span /><span /><span />
                    </div>
                    <Container className="py-lg-md d-flex">
                        <div className="col px-0">
                            <Row>
                                <Col lg="8">
                                    <h1 className="display-3 text-white">
                                        Marketing tools<br />
                                        <span>create QR campaign</span>
                                    </h1>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                    <div className="separator separator-bottom separator-skew">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            preserveAspectRatio="none"
                            viewBox="0 0 2560 100"
                        >
                            <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                        </svg>
                    </div>
                </section>

                {/* MAIN PANEL */}
                <section className="section section-lg pt-lg-0 mt--200">
                    <Container>
                        <Row className="justify-content-center">
                            <Col lg="12">
                                <Card className="shadow border-0">
                                    <CardBody className="py-5">
                                        {/* NAV */}
                                        <Button
                                            color="link"
                                            className="mb-4 p-0"
                                            onClick={() => navigate("/admin/marketing")}
                                        >
                                            &larr; Back to campaigns
                                        </Button>

                                        {error && (
                                            <Alert color="danger" className="mb-3">
                                                {error}
                                            </Alert>
                                        )}

                                        {success && (
                                            <Alert color="success" className="mb-3">
                                                QR campaign saved successfully.
                                            </Alert>
                                        )}

                                        <Form>
                                            <Row>
                                                {/* LEFT: FORM */}
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label>Base URL</Label>
                                                        <Input
                                                            value={form.baseUrl}
                                                            onChange={(e) =>
                                                                setForm({ ...form, baseUrl: e.target.value })
                                                            }
                                                        />
                                                        <small className="text-muted">
                                                            The URL that the QR code will point to. Default is
                                                            https://theworshipbible.church but you can change this if needed
                                                            (e.g. to point to a specific landing page).
                                                        </small>
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>UTM Campaign <b>(Required)</b></Label>
                                                        <Input
                                                            placeholder="e.g. lent2026flyer"
                                                            value={form.utmCampaign}
                                                            onChange={(e) =>
                                                                setForm({
                                                                    ...form,
                                                                    utmCampaign: sanitiseWhileTyping(e.target.value)
                                                                })
                                                            }
                                                            onBlur={() =>
                                                                setForm({
                                                                    ...form,
                                                                    utmCampaign: normaliseCampaign(form.utmCampaign)
                                                                })
                                                            }
                                                        />
                                                        <small className="text-muted">
                                                            Normalised automatically (recommended for GA).<br />
                                                            Saved as:{" "}
                                                            <code className="text-primary">
                                                                {normalisedPreview || "—"}
                                                            </code>
                                                            <br />
                                                            e.g. "Lent 2026 Flyer" becomes "lent_2026_flyer"
                                                        </small>
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>UTM Source</Label>
                                                        <Input
                                                            value={form.utmSource}
                                                            onChange={(e) =>
                                                                setForm({ ...form, utmSource: e.target.value })
                                                            }
                                                        />
                                                        <small className="text-muted">
                                                            e.g. facebook, email, instagram, twitter, linkedin, tiktok,
                                                            pinterest, youtube, admin
                                                        </small>
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>UTM Medium</Label>
                                                        <Input
                                                            value={form.utmMedium}
                                                            onChange={(e) =>
                                                                setForm({ ...form, utmMedium: e.target.value })
                                                            }
                                                        />
                                                        <small className="text-muted">
                                                            e.g. social, paid_social, email, print, qr, cpc, banner, referral
                                                        </small>
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>UTM Content</Label>
                                                        <Input
                                                            value={form.utmContent}
                                                            onChange={(e) =>
                                                                setForm({ ...form, utmContent: e.target.value })
                                                            }
                                                        />
                                                        <small className="text-muted">
                                                            e.g. feed_post_image, feed_post_video, story_video, reel_short,
                                                            carousel_slide_1
                                                        </small>
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>UTM Term</Label>
                                                        <Input
                                                            value={form.utmTerm}
                                                            onChange={(e) =>
                                                                setForm({ ...form, utmTerm: e.target.value })
                                                            }
                                                        />
                                                        <small className="text-muted">
                                                            e.g. church_leaders, worship_leaders, lookalike_church_admins,
                                                            interest_christian_music
                                                        </small>
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>Intended Use</Label>
                                                        <Input
                                                            value={form.intendedUse}
                                                            onChange={(e) =>
                                                                setForm({ ...form, intendedUse: e.target.value })
                                                            }
                                                        />
                                                        <small className="text-muted">
                                                            Give a short description of where/how this QR code will be used
                                                            (for internal reference only).<br />
                                                            e.g. "Flyer handed out at Lent 2026 event", "Poster in church foyer",
                                                            "Link in Facebook ad"
                                                        </small>
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>Notes</Label>
                                                        <Input
                                                            type="textarea"
                                                            value={form.notes}
                                                            onChange={(e) =>
                                                                setForm({ ...form, notes: e.target.value })
                                                            }
                                                        />
                                                        <small className="text-muted">
                                                            Give any additional details or context about this campaign (for internal
                                                            reference only).<br />
                                                            e.g. "Targeting church leaders in the UK", "Part of wider Lent 2026 campaign
                                                            which includes email and social media"
                                                        </small>
                                                    </FormGroup>
                                                </Col>

                                                {/* RIGHT: PREVIEW */}
                                                <Col md={6} className="text-center">
                                                    <div
                                                        ref={qrRef}
                                                        className="bg-white p-4 rounded shadow-sm d-inline-block"
                                                        style={{ position: "relative" }}
                                                    >
                                                        <QRCodeCanvas
                                                            value={buildUrl()}
                                                            size={260}
                                                            level="H"
                                                            includeMargin
                                                            fgColor="#5E72E4"
                                                            bgColor="#ffffff"
                                                        />

                                                        <img
                                                            src="/qr/worship-bible-qr-logo.png"
                                                            alt="Worship Bible"
                                                            style={{
                                                                position: "absolute",
                                                                top: "50%",
                                                                left: "50%",
                                                                width: 64,
                                                                height: 64,
                                                                transform: "translate(-50%, -50%)",
                                                                background: "white",
                                                                padding: 6,
                                                                borderRadius: 8
                                                            }}
                                                        />
                                                    </div>

                                                    <p className="small mt-3 text-muted text-break">
                                                        {buildUrl()}
                                                    </p>

                                                    <div className="d-flex justify-content-center gap-2 mt-3">
                                                        <Button
                                                            color="primary"
                                                            onClick={downloadQr}
                                                            disabled={!normaliseCampaign(form.utmCampaign) || saving}
                                                        >
                                                            {saving ? "Saving…" : "Save & Download QR"}
                                                        </Button>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Form>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </div>
        </>
    );
}